import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col items-center space-y-3 md:flex-row md:space-x-3 md:space-y-0" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", null, "Save Changes", -1)
const _hoisted_3 = { class: "mt-6 text-lg font-medium md:text-2xl text-neutral-600" }
const _hoisted_4 = { class: "block font-semibold md:inline-block text-neutral-900" }
const _hoisted_5 = { class: "w-full p-4 mx-auto my-6 border border-gray-200 rounded lg:p-6" }
const _hoisted_6 = { class: "flex items-center justify-between" }

import { computed, onMounted, ref, watch } from "vue";
import UserLayout from "@/layouts/UserLayout.vue";
import SectionTitle from "@/components/partials/SectionTitle.vue";
import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
import SuccessModal from "@/components/modals/SuccessModal.vue";
import FormSection from "./FormSection.vue";
import { useRouter, useRoute } from "vue-router";
import PerpetratorService from "@/classes/Perpetrators/PerpetratorService";
import { renderSelect, renderSelectV2 } from "@/interfaces/DropdownInterface";
import { PerpetratorCreate } from "@/interfaces/Cases/Perpetrator";
import PerpetratorForm from "@/classes/Forms/Cases/PerpetratorForm";
import { errorResponse } from "@/helpers/errorMessage";
import { useNetworkStore } from "@/store/network";
import pickBy from "lodash/pickBy"; 
import SqliteRecordChecker from "@/classes/Forms/SqliteRecordChecker";
import PageLoader from "@/components/loaders/PageLoader.vue"
import { useRecordStatusChecker } from "@/store/recordStatusChecker"; 
import { onIonViewWillEnter } from "@ionic/vue"; 
import PerpetratorOfflineService from "@/classes/Perpetrators/Offline/OfflineService";
import PerpetratorOfflineForm from "@/classes/Perpetrators/Offline/Formatters/PerpetratorOfflineForm";
import Rules from "@/classes/Perpetrators/Offline/Validation/Rules";
import useVuelidate from "@vuelidate/core";
import ErrorValidation from "@/views/interventions/Components/Classess/ErrorValidation";
import ErrorModal from "@/components/modals/ErrorModal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'CreatePage',
  setup(__props) {

const networkStore = useNetworkStore(); 
const isNetworkAvailable = computed(() => networkStore.isNetworkAvailable);
const checkRecord = new SqliteRecordChecker(); 
const router = useRouter();
const route = useRoute();
const showSuccess = ref<boolean>(false);
const loading = ref<boolean>(false); 
const patient = ref<string>('');
const patientId = ref<number | null>(route.params.patient);
const caseId = ref<number | null>(route.params.case);
const genders = ref<renderSelectV2[]>([]);
const identities = ref<renderSelectV2[]>([]);
const nationalities = ref<renderSelect[]>([]);
const religions = ref<renderSelect[]>([]);
const relationships = ref<renderSelect[]>([]);
const currentLocations = ref<renderSelect[]>([]);
const formRef = ref<any>([]); 
const form = ref<PerpetratorCreate>(PerpetratorOfflineForm.formStructure());
const { createRules } = Rules;

const errors = ref<object>({});
const successMessage = ref<string>("New perpetrator has been added");
const item = ref<PerpetratorCreate>(PerpetratorOfflineForm.formStructure()); 
const recordStatusChecker = useRecordStatusChecker();
const showError = ref(false);
const showErrorMessage = ref(null);

/**
 * Validate the form using Vuelidator
 */
 const validateForm = async () =>{
	const vuelidate = useVuelidate(createRules({...form.value}), form.value)
	const result = await vuelidate.value.$validate();

    if (result) {
        return null;
    } else {
        showError.value = true;
        showErrorMessage.value = 'Please fill up all required fields';
        errors.value = ErrorValidation.getErrors(vuelidate.value);
        errorResponse(errors.value);
        return errors.value;
    }
}

const save = async (): Promise<any> => {
	loading.value = true;
	errors.value = {};
    showError.value = false;
    showErrorMessage.value = null;

    // Set validation rule for Online and Offline
    const validate = await validateForm();
    if (validate) {
        loading.value = false;
        return;
    }

    const response = await PerpetratorService.store(
		patientId.value,
		caseId.value,
		{ ...form.value },
        isNetworkAvailable.value,
	);

    if (response?.status === 200 || response?.data != undefined) { 
        const data = response.data.data;
        // if (isNetworkAvailable.value) {
        //     await PerpetratorOfflineService.syncToOffline(patientId.value, caseId.value, data.item);
        // }
        resetForm()
        loading.value = false;
        showSuccess.value = true;
        successMessage.value = response.data.message;
        recordStatusChecker.isCreatedPerpetrator = true;
    } else {
        loading.value = false;
        const error = response;
        errors.value = errorResponse(error.response?.data?.errors);
    }
};

const redirectSuccess = (): void => {
	showSuccess.value = false;
	router.push(
		`/patient/${patientId.value}/cases/${caseId.value}/edit`
	);
};

const getData = async (): Promise<any> => {
    const response = await PerpetratorService.create(
		patientId.value,
		caseId.value,
		pickBy({}),
        isNetworkAvailable.value
	);

    if (response?.response?.status === 404) {
        return;
    }

    if (response?.status === 200 || response.data != undefined) {
		genders.value = response.data.genders;
		identities.value = response.data.identities;
		nationalities.value = response.data.nationalities;
		religions.value = response.data.religions;
		relationships.value = response.data.perpetratorRelationships;
		currentLocations.value = response.data.currentLocations;
		form.value.is_minor = false;
		form.value.tag = response.data.unknownTag;
		patient.value = response.data.patient;
		item.value = { tag: form.value.tag };
       
    } else {
        console.log(response);
    }
};

const resetForm = () => {
    form.value.tag = null;
	form.value.details = null;
	form.value.identity = null;
	form.value.is_minor = null;
	form.value.last_name = null;
	form.value.first_name = null;
	form.value.middle_name = null;
	form.value.gender = null;
	form.value.birthday_classification = null;
	form.value.birthdate = null;
	form.value.approximate_age_year = null;
	form.value.approximate_age_month = null;
	form.value.perpetrator_relationship_id = null;
	form.value.other_relationship = null;
	form.value.nickname = null;
	form.value.occupation = null;
	form.value.nationality_id = null;
	form.value.other_nationality = null;
	form.value.religion_id = null;
	form.value.current_location = null;
	form.value.perpetrator_location = null;
	form.value.about_perpetrator = null;
	form.value.contacts = null;

	formRef.value.birthdateForm.datePickerData.picker.clearValue()
 
}

watch(
    () => isNetworkAvailable.value,
    () => {
        loading.value = false;
        getData();
    }
);
 

onIonViewWillEnter(async () => {
    recordStatusChecker.isCreatedPerpetrator = false;  
    resetForm();
    await getData();
});

onMounted(async () => {
	recordStatusChecker.isCreatedPerpetrator = false; 
    resetForm();
    await getData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(UserLayout, {
    hasBack: "",
    backLink: `/patient/${patientId.value}/cases/${caseId.value}/edit`,
    hasBackClass: "flex md:justify-between md:items-center md:flex-row flex-col"
  }, {
    lowerRight: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("a", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (
						_unref(router).push(
							`/patient/${patientId.value}/cases/${caseId.value}/edit`
						)
					)),
          class: "w-full mt-6 md:w-auto md:mt-0"
        }, [
          _createVNode(ButtonComponent, {
            btnWidth: "quinary",
            customClass: "quinary md:w-auto w-full"
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Cancel ")
            ]),
            _: 1
          })
        ]),
        _createVNode(ButtonComponent, {
          customClass: "primary md:w-auto w-full md:mt-0 mt-6",
          form: "form",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (save()))
        }, {
          default: _withCtx(() => [
            _hoisted_2
          ]),
          _: 1
        })
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_3, [
        _createTextVNode("Patient's Name: "),
        _createElementVNode("span", _hoisted_4, _toDisplayString(patient.value), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(SectionTitle, { title: "Perpetrator Details" })
        ]),
        _createVNode(FormSection, {
          ref_key: "formRef",
          ref: formRef,
          item: item.value,
          form: form.value,
          errors: errors.value,
          genders: genders.value,
          relationships: relationships.value,
          nationalities: nationalities.value,
          religions: religions.value,
          currentLocations: currentLocations.value,
          identities: identities.value
        }, null, 8, ["item", "form", "errors", "genders", "relationships", "nationalities", "religions", "currentLocations", "identities"])
      ]),
      _createVNode(SuccessModal, {
        show: showSuccess.value,
        onConfirm: _cache[2] || (_cache[2] = ($event: any) => (redirectSuccess())),
        "action-text": "Close",
        title: "Record Save!",
        content: successMessage.value
      }, null, 8, ["show", "content"]),
      _createVNode(ErrorModal, {
        show: showError.value,
        content: showErrorMessage.value
      }, null, 8, ["show", "content"]),
      _createVNode(PageLoader, { show: loading.value }, null, 8, ["show"])
    ]),
    _: 1
  }, 8, ["backLink"]))
}
}

})